import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getTourProgressAction } from "../../../../redux/actions/guidedTourActions";
import { useTour } from "../../../../context/TourContext";
import { Box } from "@mui/material";
import { makeStyles } from "@mui/styles";
import {
  ACTIONS,
  BASE,
  BORDER,
  BRAND,
  ERROR,
  GREY,
  PRIMARY,
  SUCCESS,
  WARNING,
} from "../../../../utils/constants/colors";
import {
  fontSize,
  fontWeight,
  SCROLL_BAR,
  SHADOW,
} from "../../../../utils/constants/UI/uiConstants";
import { ICON_LABELS } from "../../../../utils/constants/UI/iconLabels";
import {
  AccessTimeIcon,
  ExpandMoreIcon,
  KeyboardArrowRightOutlinedIcon,
  PlayArrowIcon,
  ReplayIcon,
} from "../../../../components/Icon/MuiIcons";
import SkeletonLoader from "../../../../components/Loaders/SkeletonLoader";
import IconComponent from "../../../../components/Icon/IconComponent";
import { useSetupWizard } from "../../../../context/SetupWizardContext";
import { SETUP_STEPS } from "../../../../components/SetupWizard";
import { fetchSetupStatuses } from "../../../../utils/helpers/setupStatusUtils";
import EventEmitter from "../../../../utils/emitter/EventEmitter";
import { EMITTER_ERROR } from "../../../../utils/constants";

const TOUR_ICON_VARIANT_OVERRIDE = {
  grid_view: "outlined",
};

// ─── SVG ring geometry ────────────────────────────────────────────────────────
const RING_R = 32;
const RING_CX = 44;
const RING_CY = 44;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_R;

// ─── Helpers ─────────────────────────────────────────────────────────────────
const getBarColor = (pct) => {
  if (pct === 0) return BORDER.DEFAULT;
  if (pct <= 30) return ERROR.W_500;
  if (pct <= 80) return WARNING.W_600;
  return SUCCESS.W_500;
};

const formatTime = (secs) => {
  if (!secs || secs < 1) return null;
  const mins = Math.round(secs / 60);
  if (mins < 1) return "< 1 min";
  return `${mins} min${mins === 1 ? "" : "s"}`;
};

// ─── Styles ──────────────────────────────────────────────────────────────────
const useStyles = makeStyles({
  // Outer layout wrapper — no background so ProfileScreen's grey page bg shows through.
  // Matches ProfileForm's isProfileSettingFormMainContainer exactly.
  root: {
    margin: "16px 0px 16px 16px",
    height: "100%",
  },

  // White card — matches ProfileForm's isProfileSettingFormContainer exactly:
  // white bg, soft shadow (the "border" effect), 6px radius, fills full height.
  mainCard: {
    background: BASE.WHITE,
    boxShadow: "0px 2px 3px rgba(98, 107, 126, 0.11)",
    borderRadius: "6px",
    padding: "18px",
    height: "100%",
    overflow: "auto",
    boxSizing: "border-box",
    ...SCROLL_BAR,
  },

  // Page header (inside mainCard for consistency)
  pageTitle: {
    display: "block",
    fontSize: "17px",
    fontWeight: fontWeight.SEMIBOLD,
    color: GREY.PRIMARY,
    lineHeight: "26px",
  },
  pageSubtitle: {
    display: "block",
    fontSize: fontSize.SM,
    fontWeight: fontWeight.REGULAR,
    color: GREY.SECONDARY,
    lineHeight: "20px",
    marginTop: "3px",
  },
  // Negative horizontal margin breaks the divider out of the 24px card padding
  // so it spans edge-to-edge, matching General / other settings tabs.
  pageDivider: {
    height: "1px",
    background: BORDER.DEFAULT,
    margin: "16px -24px 20px",
  },

  // Overall progress section (tinted, inside mainCard)
  overallSection: {
    display: "flex",
    alignItems: "center",
    gap: "20px",
    padding: "18px 22px",
    background: BASE.BACKGROUND_LIGHT,
    borderRadius: "8px",
    border: `1px solid ${BORDER.DEFAULT}`,
    marginBottom: "20px",
  },
  overallMid: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    minWidth: 0,
  },
  overallTitle: {
    display: "block",
    fontSize: fontSize.MD,
    fontWeight: fontWeight.SEMIBOLD,
    color: GREY.PRIMARY,
    lineHeight: "24px",
  },
  overallSubtitle: {
    display: "block",
    fontSize: fontSize.SM,
    fontWeight: fontWeight.REGULAR,
    color: GREY.SECONDARY,
    lineHeight: "20px",
  },
  stepsBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "3px",
    flexShrink: 0,
    paddingLeft: "20px",
    borderLeft: `1px solid ${BORDER.DEFAULT}`,
  },
  stepsNums: {
    display: "flex",
    alignItems: "baseline",
    gap: "4px",
  },
  stepsNum: {
    fontSize: "26px",
    fontWeight: fontWeight.BOLD,
    color: GREY.PRIMARY,
    lineHeight: 1,
  },
  stepsDen: {
    fontSize: fontSize.SM,
    fontWeight: fontWeight.MEDIUM,
    color: GREY.TERTIARY,
    lineHeight: 1,
  },
  stepsLabel: {
    fontSize: fontSize.XS,
    fontWeight: fontWeight.SEMIBOLD,
    color: GREY.TERTIARY,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    whiteSpace: "nowrap",
  },

  // Section head (Guided Tours label + rule + CTA)
  sectionHead: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "10px",
  },
  sectionHeadText: {
    fontSize: fontSize.XS,
    fontWeight: fontWeight.SEMIBOLD,
    color: GREY.TERTIARY,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  },
  sectionHeadLine: {
    flex: 1,
  },
  takeAllBtn: {
    display: "flex",
    alignItems: "center",
    gap: "5px",
    background: BRAND.PRIMARY,
    color: BASE.WHITE,
    border: "none",
    borderRadius: "7px",
    padding: "6px 14px",
    fontSize: fontSize.SM,
    fontWeight: fontWeight.SEMIBOLD,
    lineHeight: "20px",
    cursor: "pointer",
    whiteSpace: "nowrap",
    flexShrink: 0,
    letterSpacing: "0.01em",
    transition: "background 0.15s ease",
    "&:hover": { background: "#E03030" },
  },

  // Tour row — solo or group header (full width)
  tourRow: {
    display: "flex",
    alignItems: "center",
    gap: "14px",
    padding: "10px 14px",
    background: BASE.WHITE,
    border: `1px solid ${BORDER.DEFAULT}`,
    borderRadius: "8px",
    marginBottom: "6px",
    transition: "border-color 0.15s ease, box-shadow 0.15s ease",
    "&:hover": { borderColor: BORDER.HOVER, boxShadow: SHADOW.xs },
  },

  // Sub-tour row — indented by 24px to show hierarchy
  subTourRow: {
    display: "flex",
    alignItems: "center",
    gap: "14px",
    padding: "10px 14px",
    background: BASE.WHITE,
    border: `1px solid ${BORDER.DEFAULT}`,
    borderRadius: "8px",
    marginBottom: "6px",
    marginLeft: "24px",
    transition: "border-color 0.15s ease, box-shadow 0.15s ease",
    "&:hover": { borderColor: BORDER.HOVER, boxShadow: SHADOW.xs },
  },

  // Gutter column (24px) — holds the chevron for groups, blank for solo tours
  gutterCol: {
    width: "24px",
    height: "24px",
    flexShrink: 0,
  },

  // Chevron toggle button (inside gutter for group rows)
  chevronBtn: {
    width: "24px",
    height: "24px",
    background: "transparent",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: GREY.TERTIARY,
    borderRadius: "4px",
    padding: 0,
    flexShrink: 0,
    transition: "color 0.15s ease, background 0.15s ease",
    "&:hover": { background: ACTIONS.DEFAULT, color: GREY.SECONDARY },
  },

  // Tour name + time label
  nameCol: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    gap: "5px",
    overflow: "hidden",
  },
  tourName: {
    fontSize: "13px",
    fontWeight: fontWeight.SEMIBOLD,
    color: GREY.PRIMARY,
    lineHeight: "20px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  tourTimeDot: {
    fontSize: "10px",
    color: GREY.TERTIARY,
    lineHeight: 1,
    flexShrink: 0,
    userSelect: "none",
  },
  tourTimeWrap: {
    display: "flex",
    alignItems: "center",
    gap: "3px",
    flexShrink: 0,
  },
  tourTime: {
    fontSize: fontSize.XS,
    color: GREY.TERTIARY,
    lineHeight: "18px",
    whiteSpace: "nowrap",
  },

  // Icon badge — left side of each tour row
  tourIconBadge: {
    width: "42px",
    height: "42px",
    background: PRIMARY.W_50,
    border: `1px solid ${PRIMARY.W_100}`,
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  // Right side group: bar + % + play
  rowRight: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexShrink: 0,
  },

  // Progress bar — percentage of the flex:1 column (stable because actionBtnsWrap has fixed minWidth)
  miniBar: {
    width: "30%",
    height: "5px",
    flexShrink: 0,
    background: BORDER.LIGHT,
    borderRadius: "20px",
    overflow: "hidden",
  },
  miniBarFill: {
    height: "100%",
    borderRadius: "20px",
    transition: "width 0.4s ease",
  },

  // Percentage label to the right of bar
  rowPct: {
    fontSize: fontSize.XS,
    fontWeight: fontWeight.SEMIBOLD,
    color: GREY.SECONDARY,
    lineHeight: "18px",
    whiteSpace: "nowrap",
    minWidth: "34px",
    textAlign: "right",
  },

  // Wrapper for the inline action buttons — fixed width so 1-button and 2-button
  // rows reserve the same space, keeping the flex:1 name column (and its bar) stable
  actionBtnsWrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "6px",
    flexShrink: 0,
    minWidth: "210px",
  },

  // Resume — green theme, || icon
  actionBtnResume: {
    display: "flex",
    alignItems: "center",
    gap: "5px",
    background: SUCCESS.W_500,
    color: BASE.WHITE,
    border: "none",
    borderRadius: "7px",
    padding: "5px 11px",
    fontSize: "11px",
    fontWeight: fontWeight.SEMIBOLD,
    cursor: "pointer",
    whiteSpace: "nowrap",
    lineHeight: "18px",
    letterSpacing: "0.01em",
    fontFamily: "inherit",
    transition: "background 0.15s ease",
    "&:hover": { background: SUCCESS.W_600 },
  },

  // Start / Start Over — brand theme, play or replay icon
  actionBtnBrand: {
    display: "flex",
    alignItems: "center",
    gap: "5px",
    background: BRAND.PRIMARY,
    color: BASE.WHITE,
    border: "none",
    borderRadius: "7px",
    padding: "5px 11px",
    fontSize: "11px",
    fontWeight: fontWeight.SEMIBOLD,
    cursor: "pointer",
    whiteSpace: "nowrap",
    lineHeight: "18px",
    letterSpacing: "0.01em",
    fontFamily: "inherit",
    transition: "background 0.15s ease",
    "&:hover": { background: "#E03030" },
  },

  // Retake — outlined white, replay icon
  actionBtnRetake: {
    display: "flex",
    alignItems: "center",
    gap: "5px",
    background: BASE.WHITE,
    color: GREY.SECONDARY,
    border: `1px solid ${BORDER.DARK}`,
    borderRadius: "7px",
    padding: "5px 11px",
    fontSize: "11px",
    fontWeight: fontWeight.SEMIBOLD,
    cursor: "pointer",
    whiteSpace: "nowrap",
    lineHeight: "18px",
    letterSpacing: "0.01em",
    fontFamily: "inherit",
    transition: "background 0.15s ease",
    "&:hover": { background: ACTIONS.DEFAULT },
  },
});


// ─── Component ────────────────────────────────────────────────────────────────
const LearningCenter = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { startTour, startPipeline, setGlobalTourSequence } = useTour();
  const { startWizardFromLearningCenter } = useSetupWizard();
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [setupStatuses, setSetupStatuses] = useState(null); // null = loading, {} = loaded
  const [setupLoading, setSetupLoading] = useState(true);

  const { loading, data, error } = useSelector((state) => state.tourProgress);

  useEffect(() => {
    dispatch(getTourProgressAction(navigate));
  }, [dispatch]);

  useEffect(() => {
    const handleRefresh = () => dispatch(getTourProgressAction(navigate));
    window.addEventListener("__tour:skipModalClosed", handleRefresh);
    return () => window.removeEventListener("__tour:skipModalClosed", handleRefresh);
  }, [dispatch, navigate]);

  useEffect(() => {
    let cancelled = false;
    fetchSetupStatuses().then((statuses) => {
      if (!cancelled) {
        setSetupStatuses(statuses);
        setSetupLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, []);

  const tours = data?.data?.tours ?? [];

  // Tour-only steps from the API
  const tourStepsNum = data?.data?.total_completed_steps ?? 0;
  const tourStepsDen = data?.data?.total_steps ?? 0;

  // Blend in the 4 account-setup steps once setup statuses are loaded.
  // While setupLoading is true, doneSetupCount = 0 and the ring shows tour-only
  // progress; it animates to the correct composite value once statuses arrive.
  const doneSetupCount = setupStatuses
    ? Object.values(setupStatuses).filter(Boolean).length
    : 0;
  const total_completed_steps = tourStepsNum + doneSetupCount;
  const total_steps = tourStepsDen + 4;
  const overallPct = total_steps > 0
    ? Math.round((total_completed_steps / total_steps) * 100)
    : 0;

  // Arc starts at 12-o'clock (rotate -90°), fills clockwise
  const dashOffset = RING_CIRCUMFERENCE * (1 - overallPct / 100);

  const isExpanded = (id) => !collapsedGroups[id];
  const toggleGroup = (id) =>
    setCollapsedGroups((prev) => ({ ...prev, [id]: !prev[id] }));

  // Analyses a parent tour's sub_tours and returns { primary, secondary } play actions.
  // Each action has { label, pipeline } where pipeline is [{ key, fromStep, name }].
  //
  // Progress states and their outcomes:
  //   All 0%                                       → primary "Start" (full pipeline)
  //   All 100%                                     → primary "Retake" (full pipeline)
  //   Non-sequential (gap in middle)               → primary "Start Over" (full pipeline)
  //   Sequential, active at 0% (prev done)         → primary "Resume · {name}" (tail pipeline)
  //                                                   secondary "Start Over" (full pipeline)
  //   Sequential, active partial + resume_allowed  → primary "Resume · {name}" (resume pipeline)
  //                                                   secondary "Start Over" (full pipeline)
  //   Sequential, active partial + !resume_allowed → primary "Start Over · {name}" (tail pipeline)
  const getGroupMenuAction = (subTours) => {
    if (!subTours || subTours.length === 0) return null;

    const sorted = [...subTours].sort((a, b) => a.order - b.order);
    const getPct = (t) =>
      t.total_steps > 0
        ? Math.min(100, Math.round((t.completed_step_count / t.total_steps) * 100))
        : 0;

    // Full pipeline: every sub-tour from step 0 in order
    const fullPipeline = sorted.map((t) => ({ key: t.key, fromStep: 0, name: t.name }));

    // All complete → Retake (restart everything)
    if (sorted.every((t) => getPct(t) === 100)) {
      return { primary: { label: "Retake", pipeline: fullPipeline }, secondary: null };
    }

    // All at 0% → Start (run all from beginning)
    if (sorted.every((t) => getPct(t) === 0)) {
      return { primary: { label: "Start", pipeline: fullPipeline }, secondary: null };
    }

    // Boundary: first sub-tour that isn't 100%
    const boundaryIdx = sorted.findIndex((t) => getPct(t) < 100);

    // Sequential check: every sub-tour after the boundary must be untouched (0%)
    const isSequential = sorted
      .slice(boundaryIdx + 1)
      .every((t) => getPct(t) === 0);

    if (!isSequential) {
      // Non-sequential progress → Start Over from the very beginning
      return { primary: { label: "Start Over", pipeline: fullPipeline }, secondary: null };
    }

    const active = sorted[boundaryIdx];
    const activePct = getPct(active);

    // Tail pipeline: from the active sub-tour onward (all at step 0)
    const tailPipeline = sorted
      .slice(boundaryIdx)
      .map((t) => ({ key: t.key, fromStep: 0, name: t.name }));

    if (activePct === 0) {
      // Previous sub-tours done, active one not started yet.
      // "Resume · {name}" continues the group from where progress left off.
      return {
        primary: { label: `Resume · ${active.name}`, pipeline: tailPipeline },
        secondary: { label: "Start Over", pipeline: fullPipeline },
      };
    }

    // Active sub-tour has partial progress
    if (active.resume_allowed) {
      const resumePipeline = [
        { key: active.key, fromStep: active.completed_step_count, name: active.name },
        ...sorted.slice(boundaryIdx + 1).map((t) => ({ key: t.key, fromStep: 0, name: t.name })),
      ];
      return {
        primary: { label: `Resume · ${active.name}`, pipeline: resumePipeline },
        secondary: { label: "Start Over", pipeline: fullPipeline },
      };
    }

    // Not resume_allowed: restart the active sub-tour from step 0, run remaining
    return {
      primary: { label: `Start Over · ${active.name}`, pipeline: tailPipeline },
      secondary: null,
    };
  };

  // Builds a flat pipeline of every leaf tour (sub-tours of parent tours, solo tours
  // as-is), all starting from step 0, sorted by order. Used by "Take Complete Tour".
  const buildCompletePipeline = (allTours) => {
    if (!allTours || allTours.length === 0) return [];
    return [...allTours]
      .sort((a, b) => a.order - b.order)
      .flatMap((t) =>
        t.sub_tours && t.sub_tours.length > 0
          ? [...t.sub_tours]
              .sort((a, b) => a.order - b.order)
              .map((s) => ({ key: s.key, fromStep: 0, name: s.name }))
          : [{ key: t.key, fromStep: 0, name: t.name }],
      );
  };

  // Keep the global tour sequence in sync so GuidedTourProvider knows what
  // comes "next" when a partial/singular tour finishes normally.
  // Placed here so both `tours` and `buildCompletePipeline` are in scope.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (tours.length > 0) {
      setGlobalTourSequence(buildCompletePipeline(tours));
    }
  }, [tours]);

  // Renders inline action buttons for each tour row.
  // Logic for which actions appear is unchanged — only presentation differs.
  const renderActionButtons = (tour, isGroup, pct) => {
    const makeBtn = (label, onClick) => {
      const isResume = label.startsWith("Resume");
      const isRetake = label === "Retake";
      const isStartOver = label.startsWith("Start Over");

      const btnClass = isResume
        ? classes.actionBtnResume
        : isRetake
        ? classes.actionBtnRetake
        : classes.actionBtnBrand;

      const icon = isResume ? (
        <PlayArrowIcon style={{ fontSize: "13px" }} />
      ) : isStartOver || isRetake ? (
        <ReplayIcon style={{ fontSize: "13px" }} />
      ) : (
        <PlayArrowIcon style={{ fontSize: "13px" }} />
      );

      return (
        <button key={label} className={btnClass} onClick={onClick}>
          {icon}
          {label}
        </button>
      );
    };

    if (isGroup) {
      const ga = getGroupMenuAction(tour.sub_tours);
      if (!ga) return null;
      return (
        <Box className={classes.actionBtnsWrap}>
          {makeBtn(ga.primary.label, () => startPipeline(ga.primary.pipeline))}
          {ga.secondary && makeBtn(ga.secondary.label, () => startPipeline(ga.secondary.pipeline))}
        </Box>
      );
    }

    // Solo tour or sub-tour row
    return (
      <Box className={classes.actionBtnsWrap}>
        {pct === 0 && makeBtn("Start", () => startTour(tour.key, 0))}
        {pct > 0 && pct < 100 && tour.resume_allowed &&
          makeBtn("Resume", () => startTour(tour.key, tour.completed_step_count))}
        {pct > 0 && pct < 100 &&
          makeBtn("Start Over", () => startTour(tour.key, 0))}
        {pct === 100 && makeBtn("Retake", () => startTour(tour.key, 0))}
      </Box>
    );
  };

  // Shared row content: gutter | name (time) | bar + % + play
  const renderRow = (tour, isGroup, isSubTour) => {
    const pct =
      tour.total_steps > 0
        ? Math.round((tour.completed_step_count / tour.total_steps) * 100)
        : 0;
    const barColor = getBarColor(pct);
    const timeLabel = formatTime(tour.avg_time_secs);

    return (
      <>
        {/* Icon badge */}
        <Box className={classes.tourIconBadge}>
          <IconComponent
            iconLabel={tour.icon || "school"}
            fontSize="22px"
            color={BRAND.PRIMARY}
            iconVariant={TOUR_ICON_VARIANT_OVERRIDE[tour.icon] || undefined}
          />
        </Box>

        {/* Name + bar column — grows to fill available space */}
        <Box style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "5px" }}>
          {/* Line 1: name + time */}
          <Box style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <span className={classes.tourName}>{tour.name}</span>
            {timeLabel && (
              <>
                <span className={classes.tourTimeDot}>·</span>
                <Box className={classes.tourTimeWrap}>
                  <AccessTimeIcon style={{ fontSize: "11px", color: GREY.TERTIARY }} />
                  <span className={classes.tourTime}>{timeLabel}</span>
                </Box>
              </>
            )}
          </Box>

          {/* Line 2: progress bar + % */}
          <Box style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Box className={classes.miniBar}>
              <Box
                className={classes.miniBarFill}
                style={{ width: `${pct}%`, background: barColor }}
              />
            </Box>
            <span className={classes.rowPct}>{pct}%</span>
          </Box>
        </Box>

        {/* Action buttons — vertically centered by tourRow's align-items: center */}
        {renderActionButtons(tour, isGroup, pct)}

        {/* Chevron toggle — groups only */}
        {isGroup && (
          <button
            className={classes.chevronBtn}
            onClick={(e) => {
              e.stopPropagation();
              toggleGroup(tour.id);
            }}
          >
            {isExpanded(tour.id) ? (
              <ExpandMoreIcon style={{ fontSize: "16px" }} />
            ) : (
              <KeyboardArrowRightOutlinedIcon style={{ fontSize: "16px" }} />
            )}
          </button>
        )}
      </>
    );
  };

  if (loading) {
    return (
      <Box className={classes.root}>
        <Box className={classes.mainCard}>
          {/* Header — always visible, not API-dependent */}
          <span className={classes.pageTitle}>Learning Center</span>
          <span className={classes.pageSubtitle}>
            Take guided tours of the key modules and features you need to know to get the most out of ReachIQ.
          </span>
          <Box className={classes.pageDivider} />

          {/* Banner skeleton — same width/height as the real overallSection */}
          <Box style={{ marginBottom: "20px" }}>
            <SkeletonLoader
              type="rounded"
              skeletonWidth="100%"
              skeletonHeight="120px"
              stackWidth="100%"
              stackHeight="120px"
            />
          </Box>

          {/* Essentials Setup skeleton */}
          <Box className={classes.sectionHead}>
            <span className={classes.sectionHeadText}>Essentials Setup (4)</span>
            <Box className={classes.sectionHeadLine} />
          </Box>
          <Box style={{ marginBottom: "20px" }}>
            <SkeletonLoader type="rounded" skeletonWidth="100%" skeletonHeight="104px" stackWidth="100%" stackHeight="104px" />
          </Box>

          {/* Section head — label + rule + disabled CTA */}
          <Box className={classes.sectionHead}>
            <span className={classes.sectionHeadText}>Guided Tours (0)</span>
            <Box className={classes.sectionHeadLine} />
            <button
              className={classes.takeAllBtn}
              disabled
              style={{ opacity: 0.6, cursor: "not-allowed" }}
              title="Tours are loading, please wait"
            >
              <PlayArrowIcon style={{ fontSize: "13px" }} />
              Take Complete Tour
            </button>
          </Box>

          {/* Tour card skeletons — 5 rows matching tourRow height */}
          {Array.from({ length: 5 }).map((_, i) => (
            <Box key={i} style={{ marginBottom: "6px" }}>
              <SkeletonLoader
                type="rounded"
                skeletonWidth="100%"
                skeletonHeight="50px"
                stackWidth="100%"
                stackHeight="50px"
              />
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className={classes.root}>
        <Box className={classes.mainCard}>
          <span className={classes.pageTitle}>Learning Center</span>
          <Box style={{ marginTop: 24, color: GREY.SECONDARY, fontSize: "14px" }}>
            Unable to load tour progress. Please refresh and try again.
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      {/* ── All content inside a single white surface card ── */}
      <Box className={classes.mainCard}>
        {/* Page header */}
        <span className={classes.pageTitle}>Learning Center</span>
        <span className={classes.pageSubtitle}>
          Take guided tours of the key modules and features you need to know to get the most out of ReachIQ.
        </span>
        <Box className={classes.pageDivider} />

        {/* Overall progress row — hold skeleton until both data sources are ready */}
        {setupLoading ? (
          <Box style={{ marginBottom: "20px" }}>
            <SkeletonLoader
              type="rounded"
              skeletonWidth="100%"
              skeletonHeight="120px"
              stackWidth="100%"
              stackHeight="120px"
            />
          </Box>
        ) : null}
        <Box className={classes.overallSection} style={setupLoading ? { display: "none" } : undefined}>
          {/* Circular SVG progress ring */}
          <svg
            width={88}
            height={88}
            viewBox="0 0 88 88"
            style={{ flexShrink: 0 }}
          >
            {/* Background track */}
            <circle
              cx={RING_CX}
              cy={RING_CY}
              r={RING_R}
              stroke={PRIMARY.W_100}
              strokeWidth={8}
              fill="none"
            />
            {/* Filled arc — rotated so it starts from top */}
            <circle
              cx={RING_CX}
              cy={RING_CY}
              r={RING_R}
              stroke={BRAND.PRIMARY}
              strokeWidth={8}
              fill="none"
              strokeLinecap="round"
              strokeDasharray={RING_CIRCUMFERENCE}
              strokeDashoffset={dashOffset}
              transform={`rotate(-90 ${RING_CX} ${RING_CY})`}
              style={{ transition: "stroke-dashoffset 0.6s ease" }}
            />
            {/* Percentage centred in the ring */}
            <text
              x={RING_CX}
              y={RING_CY}
              textAnchor="middle"
              dominantBaseline="middle"
              style={{
                fontSize: "14px",
                fontWeight: "700",
                fill: BRAND.PRIMARY,
              }}
            >
              {overallPct}%
            </text>
          </svg>

          {/* Journey title */}
          <Box className={classes.overallMid}>
            <span className={classes.overallTitle}>Your ReachIQ Journey</span>
          </Box>

          {/* Steps completed counter */}
          <Box className={classes.stepsBox}>
            <Box className={classes.stepsNums}>
              <span className={classes.stepsNum}>{total_completed_steps}</span>
              <span className={classes.stepsDen}>/ {total_steps}</span>
            </Box>
            <span className={classes.stepsLabel}>Steps Completed</span>
          </Box>
        </Box>

        {/* ── Essentials Setup section ─────────────────────────────────────── */}
        <Box className={classes.sectionHead}>
          <span className={classes.sectionHeadText}>Essentials Setup (4)</span>
          <Box className={classes.sectionHeadLine} />
        </Box>

        {setupLoading ? (
          <Box style={{ marginBottom: "20px" }}>
            <SkeletonLoader type="rounded" skeletonWidth="100%" skeletonHeight="104px" stackWidth="100%" stackHeight="104px" />
          </Box>
        ) : (
          <Box style={{ marginBottom: "20px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
            {SETUP_STEPS.map((step) => {
              const isDone = setupStatuses?.[step.key] ?? false;
              return (
                <Box
                  key={step.key}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "10px 14px",
                    background: isDone ? SUCCESS.W_50 : BASE.WHITE,
                    border: `1px solid ${isDone ? SUCCESS.W_200 : BORDER.DEFAULT}`,
                    borderRadius: "8px",
                    boxShadow: SHADOW.xs,
                  }}
                >
                  {/* Status icon */}
                  <Box style={{ flexShrink: 0, display: "flex", alignItems: "center" }}>
                    <IconComponent
                      iconLabel={isDone ? ICON_LABELS.CHECK_CIRCLE : ICON_LABELS.RADIO_BUTTON_UNCHECKED}
                      fontSize={fontSize.MD}
                      color={isDone ? SUCCESS.W_500 : GREY.DISABLED}
                    />
                  </Box>

                  {/* Label */}
                  <span style={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: "13px",
                    fontWeight: fontWeight.SEMIBOLD,
                    color: GREY.PRIMARY,
                    lineHeight: "20px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}>
                    {step.label}
                  </span>

                  {/* Status badge or CTA */}
                  {isDone ? (
                    <Box style={{
                      display: "inline-flex",
                      alignItems: "center",
                      background: SUCCESS.W_100,
                      borderRadius: "20px",
                      padding: "2px 10px",
                      flexShrink: 0,
                    }}>
                      <span style={{ fontSize: fontSize.XS, fontWeight: fontWeight.SEMIBOLD, color: SUCCESS.W_700, letterSpacing: "0.3px" }}>
                        Done
                      </span>
                    </Box>
                  ) : (
                    <button
                      onClick={() => {
                        if (step.key === "signature" && !setupStatuses?.mailbox) {
                          EventEmitter.emit(EMITTER_ERROR, "Connect a mailbox first before setting up your email signature.");
                          return;
                        }
                        startWizardFromLearningCenter(step.key);
                      }}
                      style={{
                        background: BRAND.PRIMARY,
                        color: BASE.WHITE,
                        border: "none",
                        borderRadius: "6px",
                        padding: "4px 12px",
                        fontSize: "13px",
                        fontWeight: fontWeight.SEMIBOLD,
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                        fontFamily: "inherit",
                        flexShrink: 0,
                      }}
                    >
                      Set up
                    </button>
                  )}
                </Box>
              );
            })}
          </Box>
        )}

        {/* Guided tours section head */}
        <Box className={classes.sectionHead}>
          <span className={classes.sectionHeadText}>
            Guided Tours ({tours.length})
          </span>
          <Box className={classes.sectionHeadLine} />
          <button
            className={classes.takeAllBtn}
            onClick={() => startPipeline(buildCompletePipeline(tours), true)}
          >
            <PlayArrowIcon style={{ fontSize: "13px" }} />
            Take Complete Tour
          </button>
        </Box>

        {/* Tour list */}
        {tours.map((tour) => {
          const isGroup = tour.sub_tours && tour.sub_tours.length > 0;
          return (
            <React.Fragment key={tour.id}>
              {/* Solo tour or group header row */}
              <Box className={classes.tourRow}>
                {renderRow(tour, isGroup, false)}
              </Box>

              {/* Sub-tours (indented), only when group is expanded */}
              {isGroup &&
                isExpanded(tour.id) &&
                tour.sub_tours.map((sub) => (
                  <Box key={sub.id} className={classes.subTourRow}>
                    {renderRow(sub, false, true)}
                  </Box>
                ))}
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
  );
};

export default LearningCenter;
